import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { showToast } from '~/components/Shared/EcToast/showToast';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { MODAL_REMOVE_DOCUMENT_IN_GROUP } from '~/types/modal.types';
import { ERROR, SUCCESS } from '~/types/toast.types';

const RemoveDocumentInGroupModal = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const modal = useSelector(slices.documentGroup.selectors.selectModal);
  const resetModal = () => dispatch(slices.documentGroup.actions.setModal());

  const { modalType, context } = modal || {};
  const { id } = context || {};

  const [removeDocumentInGroup, removeDocumentResult] =
    api.endpoints.removeDocumentInGroup.useMutation();
  const {
    isError: removeDocumentError,
    isSuccess: removeDocumentSuccess,
    isLoading: isRemoving,
  } = removeDocumentResult;

  useEffect(() => {
    if (removeDocumentError) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'RemoveDocumentInGroupModal.removeError',
          defaultMessage:
            'Remove document from Group failed. Please try again.',
          description: 'Error message when document removal fails',
        }),
      );
    }
  }, [removeDocumentError]);

  useEffect(() => {
    if (removeDocumentSuccess) {
      showToast(
        SUCCESS,
        intl.formatMessage({
          id: 'RemoveDocumentInGroupModal.removeSuccess',
          defaultMessage: 'Document has been successfully removed.',
          description: 'Success message when document is removed from group',
        }),
      );
    }
  }, [removeDocumentSuccess]);

  if (modalType === MODAL_REMOVE_DOCUMENT_IN_GROUP) {
    return (
      <Modal
        primaryAction={{
          text: intl.formatMessage({
            id: 'RemoveDocumentInGroupModal.removeButton',
            defaultMessage: 'Remove',
            description: 'Button text for removing document from group',
          }),
          variant: 'danger',
          isLoading: isRemoving,
          onClick: async () => {
            await removeDocumentInGroup(id).unwrap();
            resetModal();
          },
        }}
        title={intl.formatMessage({
          id: 'RemoveDocumentInGroupModal.title',
          defaultMessage: 'Remove Document from Group?',
          description: 'Modal title for removing document from group',
        })}
        isVisible={modalType === MODAL_REMOVE_DOCUMENT_IN_GROUP}
        onCancel={resetModal}
        onHide={resetModal}
      >
        <FormattedMessage
          id="RemoveDocumentInGroupModal.confirmationMessage"
          defaultMessage="Are you sure you want to remove {documentName} from this group? You won't be able to undo this action."
          description="Confirmation message for document removal. Example: documentName = 'Contract Agreement.pdf'"
          values={{
            documentName: context?.handlers[0]?.document_name || '',
          }}
        />
      </Modal>
    );
  } else {
    return null;
  }
};

export default RemoveDocumentInGroupModal;
